import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { UserGear, Key, Buildings, MapPin, GraduationCap, Eye, EyeSlash, Star } from '@phosphor-icons/react';

export default function Settings() {
  const { user, API } = useAuth(); 
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    target_exam: 'JEE',
    institution: '',
    location: '',
    target_year: '',
    ai_api_key: '',
    phone_number: '',
    phone_verified: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        display_name: user.display_name || '',
        target_exam: user.target_exam || 'JEE',
        institution: user.institution || '',
        location: user.location || '',
        target_year: user.target_year || '',
        ai_api_key: user.ai_api_key || '',
        phone_number: user.phone_number || '',
        phone_verified: user.phone_verified || false
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const payload = {...formData};
      delete payload.phone_verified;
      await axios.put(`${API}/auth/profile`, payload, { withCredentials: true });
      setMessage('Profile updated successfully! Refreshing to apply changes...');
      setTimeout(() => window.location.reload(), 1500); 
    } catch (err) {
      setMessage('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.phone_number) return alert('Enter a phone number first');
    try {
      await axios.post(`${API}/auth/send-otp`, { phone_number: formData.phone_number }, { withCredentials: true });
      setOtpSent(true);
      alert('OTP sent via SMS!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send OTP');
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) return alert('Enter the OTP');
    setVerifying(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { phone_number: formData.phone_number, code: otp }, { withCredentials: true });
      setFormData(prev => ({...prev, phone_verified: true}));
      setOtpSent(false);
      alert('Phone number verified successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="container py-8 max-w-4xl space-y-8" data-testid="settings-page">
      <div className="flex items-center gap-4">
        <UserGear size={48} weight="duotone" className="text-primary" />
        <div>
          <h1 className="text-4xl font-heading text-white uppercase tracking-widest">CANDIDATE PROFILE</h1>
          <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">Manage personal details and AI configurations</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="rounded-none border-border bg-card">
            <CardHeader className="border-b border-border bg-secondary/50">
              <CardTitle className="font-heading text-xl tracking-widest uppercase flex items-center gap-2">
                <GraduationCap className="text-primary" /> ACADEMIC DETAILS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">FULL NAME</label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
              </div>
              
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">DISPLAY NAME (ANONYMOUS ALIAS)</label>
                <Input value={formData.display_name} onChange={e => setFormData({...formData, display_name: e.target.value})} placeholder="E.g. ShadowStudent" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
                <p className="mt-2 text-xs text-muted-foreground">If set, this will be used on the Leaderboard instead of your full name.</p>
              </div>
              
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">TARGET EXAM</label>
                <select 
                  className="mt-1 w-full h-12 bg-black/50 border border-white/10 rounded-none tracking-wider text-white px-4 outline-none focus:border-primary"
                  value={formData.target_exam}
                  onChange={(e) => setFormData({...formData, target_exam: e.target.value})}
                >
                  <option value="JEE">JEE</option>
                  <option value="NEET">NEET</option>
                  <option value="IAS">IAS</option>
                  <option value="CAT">CAT</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1"><Buildings size={14}/> INSTITUTION / COLLEGE</label>
                <Input value={formData.institution} onChange={e => setFormData({...formData, institution: e.target.value})} placeholder="Where are you studying?" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
              </div>

              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1"><MapPin size={14}/> LOCATION / CITY</label>
                <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="E.g. Mumbai, Delhi" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
              </div>

              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">TARGET YEAR</label>
                <Input type="number" value={formData.target_year} onChange={e => setFormData({...formData, target_year: e.target.value})} placeholder="E.g. 2025" className="mt-1 rounded-none border-border bg-black/50 h-12 text-white" />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-border bg-card h-fit">
            <CardHeader className="border-b border-border bg-secondary/50">
              <CardTitle className="font-heading text-xl tracking-widest uppercase flex items-center gap-2">
                <Key className="text-accent" /> AI INTEGRATION
              </CardTitle>
              <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Your API key is required to use AI features</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">PERSONAL GEMINI API KEY <span className="text-destructive">*</span></label>
                <div className="relative mt-1">
                  <Input 
                    required 
                    type={showApiKey ? "text" : "password"} 
                    value={formData.ai_api_key} 
                    onChange={e => setFormData({...formData, ai_api_key: e.target.value})} 
                    placeholder="AIzaSy..." 
                    className="rounded-none border-border bg-black/50 h-12 text-white pr-10" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowApiKey(!showApiKey)} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  >
                    {showApiKey ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Required to generate Mock Tests and interact with the AI Coach. Get your free key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-primary hover:underline font-bold">Google AI Studio</a>.</p>
              </div>

              <div className="pt-4 border-t border-border">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">PHONE VERIFICATION (OPTIONAL)</label>
                <div className="flex gap-2 mt-2">
                  <Input 
                    type="tel" 
                    placeholder="+1234567890" 
                    value={formData.phone_number} 
                    onChange={e => setFormData({...formData, phone_number: e.target.value})} 
                    disabled={formData.phone_verified}
                    className={`rounded-none border-border bg-black/50 h-12 text-white ${formData.phone_verified ? 'opacity-50' : ''}`} 
                  />
                  {!formData.phone_verified && (
                    <Button type="button" onClick={handleSendOTP} disabled={!formData.phone_number} className="h-12 rounded-none font-bold tracking-widest uppercase bg-chart-4 text-black hover:bg-chart-4/80">
                      SEND OTP
                    </Button>
                  )}
                  {formData.phone_verified && (
                    <div className="flex items-center h-12 px-4 bg-chart-5/10 text-chart-5 border border-chart-5/30 text-xs font-bold tracking-widest uppercase">
                      VERIFIED
                    </div>
                  )}
                </div>

                {otpSent && !formData.phone_verified && (
                  <div className="flex gap-2 mt-2">
                    <Input 
                      type="text" 
                      placeholder="ENTER 6-DIGIT OTP" 
                      value={otp} 
                      onChange={e => setOtp(e.target.value)} 
                      className="rounded-none border-border bg-black/50 h-12 text-white" 
                    />
                    <Button type="button" onClick={handleVerifyOTP} disabled={verifying || !otp} className="h-12 rounded-none font-bold tracking-widest uppercase bg-primary hover:bg-primary/80">
                      {verifying ? 'VERIFYING...' : 'CONFIRM'}
                    </Button>
                  </div>
                )}
                <p className="mt-2 text-xs text-muted-foreground">Required if you want to receive daily motivational quotes and reminders via SMS.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {message && (
          <div className={`p-4 border font-bold text-sm uppercase tracking-widest ${message.includes('success') ? 'bg-primary/10 border-primary text-primary' : 'bg-destructive/10 border-destructive text-destructive'}`}>
            {message}
          </div>
        )}

        <Button type="submit" disabled={loading} className="w-full h-14 font-heading text-xl tracking-wider rounded-none bg-primary hover:bg-primary/80" data-testid="save-profile-btn">
          {loading ? 'SAVING...' : 'SAVE CONFIGURATION'}
        </Button>
      </form>

      <Card className="rounded-none border-chart-3/50 bg-chart-3/5 mt-8 hover:bg-chart-3/10 transition-colors">
        <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl text-white uppercase tracking-widest flex items-center gap-2">
              <Star size={24} weight="fill" className="text-chart-3" /> Rate Your App
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Enjoying MockRank? Leave us a review on the Play Store!</p>
          </div>
          <Button 
            onClick={() => window.open("https://play.google.com/store/apps/details?id=com.mockrankai.app", "_blank")}
            className="w-full sm:w-auto h-12 rounded-none font-bold tracking-widest uppercase bg-chart-3 text-black hover:bg-chart-3/80"
          >
            RATE ON GOOGLE PLAY
          </Button>
        </CardContent>
      </Card>

      <div className="pt-12 mt-12 border-t border-border flex flex-col items-center opacity-50 hover:opacity-100 transition-opacity">
        <button 
          onClick={async () => {
            if(window.confirm("CRITICAL WARNING: This will permanently delete your account, wipe your personal information, and anonymize your test scores. This action CANNOT be undone. Are you absolutely sure?")) {
              if(window.confirm("Final confirmation: Type OK to delete (just kidding, clicking OK will delete it).")) {
                try {
                  await axios.delete(`${API}/auth/profile`, { withCredentials: true });
                  window.location.href = "/login";
                } catch(e) {
                  alert("Failed to delete account");
                }
              }
            }
          }}
          className="text-xs text-destructive hover:underline tracking-widest uppercase font-bold"
          data-testid="delete-account-btn"
        >
          Danger Zone: Permanently Delete Account
        </button>
      </div>
    </div>
  );
}